import {
  AssessmentUnitStimulusBlock,
  CardData,
  CardType,
  MAX_WIDTH_FOR_ANSWER_AND_FEEDBACK_CONTENT,
  MAX_WIDTH_FOR_MCAT_PASSAGE_REVIEW_CONTENT,
  QuestionGenerationTemplateId,
} from "../constants/GlobalTypesAndEnumsAndConstants";

export const getAssessmentUnitTableRowCells = (row: string[] | { cells: string[] }): string[] =>
  Array.isArray(row) ? row : row.cells;

export const normalizeAssessmentUnitStimulusBlocks = (
  blocks: AssessmentUnitStimulusBlock[]
): AssessmentUnitStimulusBlock[] =>
  blocks.map((block) => {
    if (block.type !== "table") return { ...block };
    return {
      ...block,
      columns: [...block.columns],
      rows: block.rows.map((row) => [...getAssessmentUnitTableRowCells(row)]),
    };
  });

export const buildFirestoreAssessmentUnitStimulus = (
  blocks: AssessmentUnitStimulusBlock[]
): {
  schemaVersion: number;
  blocks: AssessmentUnitStimulusBlock[];
} => {
  const normalizedBlocks = normalizeAssessmentUnitStimulusBlocks(blocks);
  const containsTable = normalizedBlocks.some((block) => block.type === "table");

  return {
    // Firestore does not allow an array directly inside another array. Schema
    // v2 wraps each table row in a map while the editor keeps simple arrays.
    schemaVersion: containsTable ? 2 : 1,
    blocks: normalizedBlocks.map((block) => {
      if (block.type !== "table") return block;
      return {
        ...block,
        rows: block.rows.map((row) => ({
          cells: [...getAssessmentUnitTableRowCells(row)],
        })),
      };
    }),
  };
};

const legacyPassagePrefix = "legacy-passage:";
const independentCardPrefix = "independent-card:";

export const getAssessmentUnitGroupingKey = (card: CardData): string => {
  if (card.assessment_unit_id) {
    return `assessment-unit:${card.assessment_unit_id}`;
  }
  if (card.case_scenario_details) {
    return `${legacyPassagePrefix}${card.case_scenario_details}`;
  }
  return `${independentCardPrefix}${card.id || card.card_id || card.flashcard_generated_id || card.card_index}`;
};

export const orderCardsByAssessmentUnit = (cards: CardData[]): CardData[] => {
  const groups = new Map<string, CardData[]>();

  cards.forEach((card) => {
    const groupKey = getAssessmentUnitGroupingKey(card);
    groups.set(groupKey, [...(groups.get(groupKey) || []), card]);
  });

  return Array.from(groups.values())
    .sort((firstGroup, secondGroup) => {
      const firstGroupIndex = Math.min(...firstGroup.map((card) => card.card_index));
      const secondGroupIndex = Math.min(...secondGroup.map((card) => card.card_index));
      return firstGroupIndex - secondGroupIndex;
    })
    .flatMap((groupCards) =>
      [...groupCards].sort((firstCard, secondCard) => {
        const firstQuestionIndex = firstCard.assessment_unit_question_index;
        const secondQuestionIndex = secondCard.assessment_unit_question_index;
        if (firstQuestionIndex != null && secondQuestionIndex != null) {
          return firstQuestionIndex - secondQuestionIndex;
        }
        return firstCard.card_index - secondCard.card_index;
      })
    );
};

export const groupCardsByAssessmentUnit = (cards: CardData[]): Record<string, CardData[]> => {
  const groupedCards: Record<string, CardData[]> = {};
  orderCardsByAssessmentUnit(cards).forEach((card) => {
    const groupKey = getAssessmentUnitGroupingKey(card);
    groupedCards[groupKey] = [...(groupedCards[groupKey] || []), card];
  });
  return groupedCards;
};

export const hasSharedAssessmentUnit = (card: CardData): boolean => {
  return card.assessment_unit_kind === "shared_stimulus" && Boolean(card.assessment_unit_id);
};

export const hasMcatSharedPassageQuestion = (card: CardData | null | undefined): boolean =>
  card?.question_generation_template_id === QuestionGenerationTemplateId.MCAT && hasSharedAssessmentUnit(card);

/**
 * Passage reviews use one stable reading width for the entire set. Keeping the
 * width stable while moving between passage and independent questions avoids a
 * distracting horizontal jump within the same review session.
 */
export const getReviewQuestionMaxWidthPixels = (
  reviewCards: CardData[] | null | undefined,
  currentCard: CardData | null | undefined
): number => {
  if (reviewCards?.some(hasMcatSharedPassageQuestion) || hasMcatSharedPassageQuestion(currentCard)) {
    return MAX_WIDTH_FOR_MCAT_PASSAGE_REVIEW_CONTENT;
  }
  if (currentCard?.card_type === CardType.USMLEStep1MCQ) return 800;
  return MAX_WIDTH_FOR_ANSWER_AND_FEEDBACK_CONTENT;
};

/**
 * Returns only the editable text representation of a shared passage. Generated
 * structured blocks are intentionally not flattened into this textarea because
 * tables, equations, and figures cannot round-trip through a plain Markdown
 * edit without losing information.
 */
export const getEditableSharedAssessmentUnitPassageMarkdown = (card: CardData | null | undefined): string => {
  return card?.assessment_unit_stimulus_override_markdown ?? card?.case_scenario_details ?? "";
};

/**
 * Distinguishes an actual passage edit from a card edit whose source passage is
 * represented only by structured stimulus blocks. In the latter case the
 * legacy passage textarea starts blank, but that blank value is not a request
 * to erase the structured passage.
 */
export const hasSharedAssessmentUnitPassageChanged = (card: CardData, editedPassageMarkdown: string): boolean => {
  if (!hasSharedAssessmentUnit(card) || !editedPassageMarkdown.trim()) {
    return false;
  }
  return editedPassageMarkdown !== getEditableSharedAssessmentUnitPassageMarkdown(card);
};

export const getEditableSharedAssessmentUnitStimulusBlocks = (
  card: CardData | null | undefined
): AssessmentUnitStimulusBlock[] => {
  if (!card) return [];
  if (card.assessment_unit_stimulus_override_markdown != null) {
    return [{ type: "markdown", text: card.assessment_unit_stimulus_override_markdown }];
  }
  if (card.assessment_unit_stimulus?.length) {
    return normalizeAssessmentUnitStimulusBlocks(card.assessment_unit_stimulus);
  }
  return [{ type: "markdown", text: card.case_scenario_details ?? "" }];
};

export const haveSharedAssessmentUnitStimulusBlocksChanged = (
  card: CardData,
  editedStimulusBlocks: AssessmentUnitStimulusBlock[]
): boolean => {
  if (!hasSharedAssessmentUnit(card)) return false;
  return (
    JSON.stringify(normalizeAssessmentUnitStimulusBlocks(editedStimulusBlocks)) !==
    JSON.stringify(getEditableSharedAssessmentUnitStimulusBlocks(card))
  );
};

export const serializeAssessmentUnitStimulusBlocksToPlainText = (blocks: AssessmentUnitStimulusBlock[]): string =>
  normalizeAssessmentUnitStimulusBlocks(blocks)
    .map((block) => {
      if (block.type === "markdown") return block.text.trim();
      if (block.type === "equation") {
        return [block.label?.trim(), block.latex.trim()].filter(Boolean).join("\n");
      }
      if (block.type === "table") {
        const tableRows = [block.columns, ...block.rows.map((row) => getAssessmentUnitTableRowCells(row))].map((row) =>
          row.join(" | ")
        );
        return [block.caption.trim(), ...tableRows].filter(Boolean).join("\n");
      }
      return [block.caption.trim(), block.alt_text.trim()].filter(Boolean).join("\n");
    })
    .filter(Boolean)
    .join("\n\n");

export const TUTOR_PASSAGE_CONTEXT_MAX_CHARS = 6000;

/**
 * Returns the exact passage representation visible to the learner, including
 * structured tables/equations/figures. The shared editor override is
 * authoritative when present, so tutor context never resurrects stale
 * generated blocks after an edit.
 */
export const getAssessmentUnitPassageTextForTutor = (
  card: CardData | null | undefined,
  maxCharacters = TUTOR_PASSAGE_CONTEXT_MAX_CHARS
): string | undefined => {
  if (!card || maxCharacters <= 0) return undefined;

  let passageText: string | undefined;
  if (card.assessment_unit_stimulus_override_markdown != null) {
    passageText = card.assessment_unit_stimulus_override_markdown;
  } else if (card.assessment_unit_stimulus?.length) {
    passageText = serializeAssessmentUnitStimulusBlocksToPlainText(card.assessment_unit_stimulus);
  } else {
    passageText = card.case_scenario_details;
  }

  const normalizedPassageText = passageText?.trim();
  return normalizedPassageText ? normalizedPassageText.slice(0, maxCharacters) : undefined;
};

/**
 * Keeps the study algorithm's unit-level order while making every shared
 * passage contiguous and restoring its authored child order. Study algorithms
 * may shuffle cards, but an MCAT passage is one assessment unit rather than a
 * collection of independently shuffled questions.
 */
export const orderCardsForReviewByAssessmentUnit = (cards: CardData[]): CardData[] => {
  const sharedCardsByUnit = new Map<string, Array<{ card: CardData; originalIndex: number }>>();

  cards.forEach((card, originalIndex) => {
    if (!hasSharedAssessmentUnit(card)) return;
    const unitId = card.assessment_unit_id as string;
    const existingUnitCards = sharedCardsByUnit.get(unitId);
    if (existingUnitCards) {
      existingUnitCards.push({ card, originalIndex });
    } else {
      sharedCardsByUnit.set(unitId, [{ card, originalIndex }]);
    }
  });

  const emittedUnitIds = new Set<string>();
  return cards.flatMap((card) => {
    if (!hasSharedAssessmentUnit(card)) return [card];

    const unitId = card.assessment_unit_id as string;
    if (emittedUnitIds.has(unitId)) return [];
    emittedUnitIds.add(unitId);

    return [...(sharedCardsByUnit.get(unitId) || [])]
      .sort((firstEntry, secondEntry) => {
        const firstQuestionIndex = firstEntry.card.assessment_unit_question_index;
        const secondQuestionIndex = secondEntry.card.assessment_unit_question_index;
        if (firstQuestionIndex != null && secondQuestionIndex != null && firstQuestionIndex !== secondQuestionIndex) {
          return firstQuestionIndex - secondQuestionIndex;
        }
        if (firstQuestionIndex != null && secondQuestionIndex == null) return -1;
        if (firstQuestionIndex == null && secondQuestionIndex != null) return 1;
        return firstEntry.originalIndex - secondEntry.originalIndex;
      })
      .map(({ card: unitCard }) => unitCard);
  });
};

/**
 * Maps a pre-grouping resume position onto the grouped review order. If an old
 * saved order points into the middle of a passage, restart that passage at its
 * first child so the student never enters at "Question 3 of 5".
 */
export const resolveAssessmentUnitAwareReviewStartIndex = (
  originalCards: CardData[],
  orderedCards: CardData[],
  originalStartIndex: number
): number => {
  if (originalStartIndex >= originalCards.length) return orderedCards.length;
  const startingCard = originalCards[Math.max(0, originalStartIndex)];
  if (!startingCard) return 0;

  if (hasSharedAssessmentUnit(startingCard)) {
    const firstUnitCardIndex = orderedCards.findIndex(
      (card) => card.assessment_unit_id === startingCard.assessment_unit_id
    );
    if (firstUnitCardIndex >= 0) return firstUnitCardIndex;
  }

  const referenceIndex = orderedCards.indexOf(startingCard);
  if (referenceIndex >= 0) return referenceIndex;
  const startingCardId = startingCard.id || startingCard.card_id || startingCard.flashcard_generated_id;
  if (!startingCardId) return Math.min(Math.max(0, originalStartIndex), orderedCards.length);
  const identityIndex = orderedCards.findIndex(
    (card) => (card.id || card.card_id || card.flashcard_generated_id) === startingCardId
  );
  return identityIndex >= 0 ? identityIndex : Math.min(Math.max(0, originalStartIndex), orderedCards.length);
};

const nonEmptyText = (value: string | undefined): string | undefined => {
  const normalizedValue = value?.trim();
  return normalizedValue ? normalizedValue : undefined;
};

const escapeHtml = (value: string): string => {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const serializeTextForHtml = (value: string): string => {
  return escapeHtml(value.trim()).replace(/\r?\n/g, "<br>");
};

/**
 * Produces the standalone passage included with each exported question.
 * Shared-unit edits take precedence so exports never resurrect stale generated
 * blocks. Legacy case-scenario cards keep their existing export behavior.
 */
export const getAssessmentUnitStimulusHtmlForExport = (card: CardData): string | undefined => {
  if (card.assessment_unit_stimulus_override_markdown != null) {
    const overrideText = nonEmptyText(card.assessment_unit_stimulus_override_markdown);
    return overrideText ? serializeTextForHtml(overrideText) : undefined;
  }

  const serializedBlocks = (card.assessment_unit_stimulus || [])
    .map((block): string | undefined => {
      if (block.type === "markdown") {
        const markdownText = nonEmptyText(block.text);
        return markdownText ? serializeTextForHtml(markdownText) : undefined;
      }

      if (block.type === "equation") {
        const equation = nonEmptyText(block.latex);
        if (!equation) return undefined;
        const label = nonEmptyText(block.label);
        const math = `\\[${escapeHtml(equation)}\\]`;
        return label ? `<strong>${escapeHtml(label)}</strong><br>${math}` : math;
      }

      if (block.type === "table") {
        if (block.columns.length === 0) return undefined;
        const columns = block.columns.map((column) => escapeHtml(column.trim()));
        const header = `<thead><tr>${columns.map((column) => `<th>${column}</th>`).join("")}</tr></thead>`;
        const rows = block.rows.map((storedRow) => {
          const row = getAssessmentUnitTableRowCells(storedRow);
          const cells = columns.map((_, columnIndex) =>
            escapeHtml((row[columnIndex] || "").replace(/\r?\n/g, " ").trim())
          );
          return `<tr>${cells.map((cell) => `<td>${cell}</td>`).join("")}</tr>`;
        });
        const table = `<table>${header}<tbody>${rows.join("")}</tbody></table>`;
        const caption = nonEmptyText(block.caption);
        return caption ? table.replace("<table>", `<table><caption>${escapeHtml(caption)}</caption>`) : table;
      }

      const caption = nonEmptyText(block.caption);
      const altText = nonEmptyText(block.alt_text) || "Source figure";
      if (block.asset_url) {
        const figure = `<img src="${escapeHtml(block.asset_url)}" alt="${escapeHtml(altText)}">`;
        return caption
          ? `<figure>${figure}<figcaption>${escapeHtml(caption)}</figcaption></figure>`
          : `<figure>${figure}</figure>`;
      }
      return caption
        ? `<strong>Figure: ${escapeHtml(caption)}</strong><br>${escapeHtml(altText)}`
        : `<strong>Figure</strong><br>${escapeHtml(altText)}`;
    })
    .filter((block): block is string => Boolean(block));

  if (serializedBlocks.length > 0) {
    return serializedBlocks.join("<br><br>");
  }

  return nonEmptyText(card.case_scenario_details);
};
